// Presse-papier — la couche d'interface.
//
// La logique est dans `src/lib/presse-papier.js` (pure, testée). Ce fichier ne
// fait que trois choses : poser un bouton « garder » sur une valeur, tenir une
// pastille flottante avec le compte, et rendre la fiche imprimable.
//
// POURQUOI CE MÉCANISME REMPLACE UN TABLEAU DE BORD
// --------------------------------------------------
// Sur un téléphone on ne voit pas tout d'un coup, alors on ASSEMBLE : on pose une
// question, on garde la réponse, on pose la suivante, on sort une fiche à la fin.
// D'où la pastille FLOTTANTE et non un onglet — elle doit rester atteignable
// depuis n'importe quel écran, sinon garder une réponse coûterait de quitter
// celui qu'on est en train de lire.
//
// Il évite aussi d'écrire un export par écran : un seul presse-papier, un seul
// générateur de fiche.

// La feuille d'impression est importée ICI et pas dans index.html : elle n'a de
// sens qu'avec ce module, et un `<link>` la chargerait même pour un utilisateur
// qui n'ouvre jamais de fiche.
import "./fiche-impression.css";

import {
  ajouter, charger, compter, construireFiche, contient, effacer,
  enregistrer, lister, retirer,
} from "../lib/presse-papier.js";
import { grouperAlertesParDestinataire } from "../lib/presse-papier-entrees.js";
import { eur2 } from "../lib/format.js";

const el = (tag, className, texte) => {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (texte != null) n.textContent = texte;
  return n;
};

// Le presse-papier survit à la fermeture de l'app : localStorage, là où la
// session vit en sessionStorage. Une réponse gardée avant d'être interrompu au
// comptoir doit être encore là au retour.
const magasin = () => (typeof localStorage !== "undefined" ? localStorage : null);

let etat = null;
const abonnes = new Set();

function diffuser() {
  for (const f of abonnes) {
    try { f(etat); } catch { /* un abonné cassé n'empêche pas les autres */ }
  }
}

function ecrire(nouvel) {
  etat = nouvel;
  enregistrer(magasin(), etat);
  diffuser();
}

/** Nombre d'entrées gardées — pour la pastille et pour les tests d'intégration. */
export function nombreGarde() {
  return compter(etat);
}

/**
 * Bouton « garder / gardé » pour une valeur affichée.
 *
 * `element` = { id, type, titre, valeur, detail, source }. L'`id` doit être
 * STABLE d'un rendu à l'autre : c'est lui qui rend l'ajout idempotent, donc qui
 * empêche un double-tap de créer deux lignes sur la fiche.
 */
export function boutonGarder(element) {
  const b = el("button", "pp-garder");
  b.type = "button";
  const peindre = () => {
    const dedans = contient(etat, element.id);
    b.classList.toggle("dedans", dedans);
    b.textContent = dedans ? "Gardé" : "Garder";
    b.setAttribute("aria-pressed", dedans ? "true" : "false");
  };
  b.addEventListener("click", (ev) => {
    // Les cartes de l'accueil sont cliquables et mènent ailleurs : sans ça, garder
    // une valeur changerait d'écran dans la foulée.
    ev.stopPropagation();
    ev.preventDefault();
    if (contient(etat, element.id)) {
      ecrire(retirer(etat, element.id));
      return;
    }
    const res = ajouter(etat, element);
    if (!res.ok) {
      // Refus à la borne : on le DIT sur le bouton lui-même. Un clic sans effet
      // et sans message se lit comme une panne.
      b.textContent = res.message || "Presse-papier plein";
      setTimeout(peindre, 2200);
      return;
    }
    ecrire(res.etat);
  });
  abonnes.add(peindre);
  peindre();
  return b;
}

// ── La fiche ────────────────────────────────────────────────────────────────

function rendreFiche(fiche, officine) {
  const racine = el("div", "fiche");

  const entete = el("header", "fiche-entete");
  if (officine) entete.appendChild(el("p", "fiche-officine", officine));
  entete.appendChild(el("h1", "fiche-titre", fiche.titre));
  entete.appendChild(el("p", "fiche-sous-titre", fiche.sousTitre));
  racine.appendChild(entete);

  const entree = (e) => {
    const art = el("article", "fiche-entree");
    art.appendChild(el("p", "fiche-entree-titre", e.titre));
    if (e.valeur != null && e.valeur !== "") art.appendChild(el("p", "fiche-valeur", String(e.valeur)));
    if (e.detail) art.appendChild(el("p", "fiche-detail", e.detail));
    if (e.source) art.appendChild(el("p", "fiche-source", e.source));
    return art;
  };

  for (const s of fiche.sections) {
    const sec = el("section", "fiche-section");
    // La feuille d'impression cible `.fiche-section[data-type="alerte"]` depuis
    // le début, mais l'attribut n'était jamais posé : la règle n'a donc jamais
    // rien coloré. Le défaut ne se voyait pas — un titre qui reste noir au lieu
    // de virer au corail ne ressemble pas à une panne.
    sec.dataset.type = s.type;
    sec.appendChild(el("h2", "fiche-section-titre", s.libelle));

    if (s.type === "alerte") {
      // Une réclamation a UN destinataire. Mélanger sur la même page les
      // conditions consenties par deux fournisseurs concurrents, c'est leur
      // montrer ce que fait l'autre — et perdre le rapport de force qu'on
      // cherchait justement à établir. Le découpage n'est donc pas une
      // commodité de lecture : c'est ce qui rend la page envoyable telle quelle.
      for (const g of grouperAlertesParDestinataire(s.entrees)) {
        const tete = g.total > 0
          ? `${g.destinataire} — ${g.nb} ligne${g.nb > 1 ? "s" : ""}, ${eur2(g.total)} réclamés`
          : `${g.destinataire} — ${g.nb} ligne${g.nb > 1 ? "s" : ""}`;
        sec.appendChild(el("h3", "fiche-groupe-titre", tete));
        for (const e of g.entrees) sec.appendChild(entree(e));
      }
    } else {
      for (const e of s.entrees) sec.appendChild(entree(e));
    }

    racine.appendChild(sec);
  }

  racine.appendChild(el("footer", "fiche-pied",
    `${fiche.meta.nbEntrees} élément${fiche.meta.nbEntrees > 1 ? "s" : ""} · généré le ${fiche.meta.genereLeFr}`));
  return racine;
}

function ouvrirFiche(officine) {
  const fond = el("div", "pp-fond no-print");
  const panneau = el("div", "pp-panneau");

  const barre = el("div", "pp-barre no-print");
  const dest = el("input", "pp-dest");
  dest.placeholder = "Pour qui ? — comptable, confrère, laboratoire…";
  barre.appendChild(dest);

  const imprimer = el("button", "pp-imprimer", "Imprimer / PDF");
  const vider = el("button", "pp-vider", "Tout retirer");
  const fermer = el("button", "pp-fermer", "Fermer");
  barre.appendChild(imprimer);
  barre.appendChild(vider);
  barre.appendChild(fermer);
  panneau.appendChild(barre);

  const zone = el("div", "pp-zone");
  panneau.appendChild(zone);

  const peindre = () => {
    if (!compter(etat)) {
      zone.replaceChildren(el("p", "muted",
        "Rien de gardé pour l'instant. Sur n'importe quel écran, « Garder » range une valeur ici."));
      return;
    }
    zone.replaceChildren(rendreFiche(construireFiche(etat, { officine, destinataire: dest.value }), officine));
  };
  dest.addEventListener("input", peindre);
  peindre();

  const partir = () => {
    document.body.classList.remove("mode-impression");
    fond.remove();
  };
  fermer.addEventListener("click", partir);
  fond.addEventListener("click", (e) => { if (e.target === fond) partir(); });
  vider.addEventListener("click", () => { effacer(magasin()); ecrire(charger(magasin())); peindre(); });

  imprimer.addEventListener("click", () => {
    // `mode-impression` masque tout sauf `.fiche` — sans lui, l'écran ouvert
    // derrière la fiche part à l'imprimante avec elle. Retiré dans tous les cas :
    // l'utilisateur peut annuler l'aperçu, et `afterprint` n'est pas garanti
    // partout sur mobile.
    document.body.classList.add("mode-impression");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("mode-impression"), 400);
    }, 60);
  });

  fond.appendChild(panneau);
  document.body.appendChild(fond);
}

/**
 * Monte la pastille flottante. À appeler une fois, au démarrage.
 * `officine` s'imprime en en-tête de la fiche.
 */
export function monterPressePapier(officine = "Pharmacie des Théâtres") {
  etat = charger(magasin());

  const pastille = el("button", "pp-pastille no-print");
  pastille.type = "button";
  pastille.setAttribute("aria-label", "Ouvrir la fiche");
  const peindre = () => {
    const n = compter(etat);
    pastille.textContent = `📋 ${n}`;
    // Cachée quand elle est vide : une pastille à zéro occupe le pouce sans
    // rien offrir, et l'app s'utilise d'une main.
    pastille.hidden = n === 0;
  };
  pastille.addEventListener("click", () => ouvrirFiche(officine));
  abonnes.add(peindre);
  peindre();
  document.body.appendChild(pastille);
  return { nombreGarde, lister: () => lister(etat) };
}
